import useApi from 'assets/hooks/api/useApi';
import usePageRouter from 'assets/hooks/pageRouter/usePageRouter';
import { useAuthStore } from 'assets/providers/authStore/Provider.AuthStore';
import { useEffect, useMemo, useState } from 'react';
import { productDocumentIndexRoute } from '../ProductDocument.Index';
import productDocumentListDealerDocumentsApi from 'models/productManagement/productDocument/listDealers/Api.ProductDocument.ListDealers';
import { defaultLimit, defaultOffset, HttpStatus } from 'config/Api.Config';
import { cloneDeep, differenceBy, xorBy } from 'lodash';
import dealerUploadProductDocumentApi from 'models/dealerManagement/dealer/uploadProductDocument/Api.Dealer.UploadProductDocument';
import { addLog } from 'assets/components/feedback/Feedback';
import { combineStrings, replaceStringTemplateWithValues } from 'assets/utils/data/String';
import { getLocales, Language } from 'assets/locales/Locale';
import dealerDeleteProductDocumentApi from 'models/dealerManagement/dealer/deleteProductDocument/Api.Dealer.DeleteProductDocument';
import { ProductDealerDocumentAddForm } from './sections/ProductDocument.DealerDocuments.Add';
import { useDataProviderPartition } from 'assets/providers/data/DataProvider';
import {
  documentTranslationDeleteApi,
  documentTranslationListApi,
  documentTranslationSaveApi,
} from 'models/localization/documentTranslation/Model.DocumentTranslation';
import { convertToArray } from 'assets/utils/data/Data';

export enum ProductDealerDocumentsAction {
  ADD = 'add',
  DELETE = 'delete',
}
export type VerifyProductDocumentsAction = Utils.VerifyExtends<
  Module.ProductManagement.ProductDocument.Actions,
  Utils.ValueOf<typeof ProductDealerDocumentsAction>
>;
export default function useProductDealerDocument() {
  const { permissions } = useAuthStore();
  const pageRouter = usePageRouter<
    Module.ProductManagement.ProductDocument.Params,
    Module.ProductManagement.ProductDocument.Query
  >({
    route: productDocumentIndexRoute,
  });
  const productDocumentId = pageRouter.params.id ? parseInt(pageRouter.params.id) : null;

  const { lang } = getLocales();
  const [currentView, setView] = useState<ProductDealerDocumentsAction | undefined>();
  const [selectedItems, setSelectedItems] = useState<Model.IProductDealerDocument[]>([]);
  const [selectedTranslatedItems, setSelectedTranslatedItems] = useState<Model.IDocumentTranslation[]>([]);
  const [{ limit, offset, distributorId }, setDealerDocumentFilters] = useState({
    limit: defaultLimit,
    offset: defaultOffset,
    distributorId: undefined,
  });

  const canView = permissions.EDIT_PRODUCT;
  const listDealerDocumentsApi = useApi({
    action: productDocumentListDealerDocumentsApi,
    default: { productDocumentId: productDocumentId },
    body: { productDocumentId } as Api.Core.ProductDocument.ListDealerDocuments.IRequest,
    wait: !canView,
  });

  const documentIds = useMemo(
    () => listDealerDocumentsApi.payload?.data.map((d) => d.id),
    [listDealerDocumentsApi.payload]
  );
  const listDocumentTranslationsApi = useApi(
    {
      action: documentTranslationListApi,
      body: { documentType: 'ProductDealerDocument', documentId: documentIds },
      wait: !canView || !documentIds?.length,
    },
    [canView, documentIds]
  );
  function reloadTranslatedDocs() {
    listDocumentTranslationsApi.execute((b) => b);
  }

  const handleUploadDealerDocumentSubmit = async (formData: ProductDealerDocumentAddForm) => {
    const dealers = formData.dealers || [];
    let uploadedFiles = 0;
    let totalFiles = 0;

    for (const dealer of dealers) {
      const docUploadResponse = await dealerUploadProductDocumentApi(
        {
          id: dealer,
          productId: formData.productId,
          productDocumentId: formData.productDocumentId,
        },
        { formFile: formData.formFile, languageIsoCode: Language.english }
      );

      if (docUploadResponse.status === HttpStatus.ok) {
        uploadedFiles += 1;
        if (formData.translatedFile) {
          const translatedDocUploadResponse = await documentTranslationSaveApi({
            documentId: docUploadResponse.payload.documentId,
            documentType: 'ProductDealerDocument',
            languageIsoCode: Language.french,
            formFile: formData.translatedFile,
          });
          if (translatedDocUploadResponse.status === HttpStatus.ok) {
            uploadedFiles += 1;
          }
          totalFiles += 1;
        }
      }
      totalFiles += 1;
    }

    listDealerDocumentsApi.execute((body) => body);

    if (totalFiles !== uploadedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get uploaded!`,
          totalFiles > 0 ? `${uploadedFiles}/${totalFiles} files uploaded!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyUploaded, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
  };

  const handleDeleteDealerDocumentSubmit = async () => {
    const productDealerDocuments = selectedItems || [];
    const translatedDocs = cloneDeep(selectedTranslatedItems) || [];
    let deletedFiles = 0;
    let totalFiles = 0;

    for (const productDealerDocument of productDealerDocuments) {
      const docDeleteReponse = await dealerDeleteProductDocumentApi({
        id: productDealerDocument.dealerId,
        productDealerDocumentId: productDealerDocument.id,
      });
      if (docDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;

      const tempDocs = listDocumentTranslationsApi.payload?.data.filter(
        (d) => d.documentId === productDealerDocument.id
      );
      const diffDocs = differenceBy(tempDocs, translatedDocs, (it) => it.id);
      translatedDocs.push(...diffDocs);
    }
    for (const translatedDoc of translatedDocs) {
      const translationDocDeleteReponse = await documentTranslationDeleteApi({
        id: translatedDoc.id,
      });
      if (translationDocDeleteReponse.status === HttpStatus.ok) {
        deletedFiles += 1;
      }
      totalFiles += 1;
    }

    listDealerDocumentsApi.execute((body) => body);

    if (totalFiles !== deletedFiles) {
      addLog({
        warning: combineStrings(
          ' ',
          `Some files did not get deleted!`,
          totalFiles > 0 ? `${deletedFiles}/${totalFiles} files deleted!` : null
        ),
      });
    } else {
      addLog({
        success: replaceStringTemplateWithValues(lang.wereSuccessfullyDeleted, {
          entity: lang.productDocuments,
        }),
      });
    }

    setView(undefined);
    setSelectedItems([]);
    setSelectedTranslatedItems([]);
  };

  const toggleItem = (productDealerDocument: Model.IProductDealerDocument) => {
    const newSelectedItemList = xorBy(selectedItems || [], [productDealerDocument], 'dealerId');
    setSelectedItems(newSelectedItemList);
  };
  const toggleTranslatedItem = (translatedDoc: Utils.SingleOrArray<Model.IDocumentTranslation>) => {
    const newSelectedItemList = xorBy(selectedTranslatedItems || [], convertToArray(translatedDoc), 'documentId');
    setSelectedTranslatedItems(newSelectedItemList);
  };

  const toggleSelectAll = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedItems(listDealerDocumentsApi.payload?.data ?? []);
    } else {
      setSelectedItems([]);
    }
  };

  const toggleSelectAllTranslations = (selectAll: boolean) => {
    if (selectAll) {
      setSelectedTranslatedItems(listDocumentTranslationsApi.payload?.data ?? []);
    } else {
      setSelectedTranslatedItems([]);
    }
  };

  const [filteredDealerDocuments, filteredDealerDocumentsCount] = useMemo(() => {
    const filteredList = listDealerDocumentsApi.payload?.data?.filter(
      (dealerDocument) => !distributorId || dealerDocument.dealer.representativeId === distributorId
    );

    return [filteredList?.slice(offset * limit, offset * limit + limit), filteredList?.length];
  }, [listDealerDocumentsApi.payload, limit, offset, distributorId]);

  useEffect(() => {
    setSelectedItems([]);
  }, [listDealerDocumentsApi.payload?.data]);

  return {
    currentView,
    setView,
    selectedItems,
    toggleItem,
    toggleSelectAll,
    selectedTranslatedItems,
    toggleTranslatedItem,
    toggleSelectAllTranslations,
    handleDeleteDealerDocumentSubmit,
    handleUploadDealerDocumentSubmit,
    permissions,
    fullDealerDocumentList: listDealerDocumentsApi?.payload?.data,
    translatedDocumentList: listDocumentTranslationsApi?.payload?.data ?? [],
    reloadTranslatedDocs,
    dealerDocumentList: {
      limit,
      offset,
      distributorId,
      setDealerDocumentFilters,
      data: filteredDealerDocuments,
      count: filteredDealerDocumentsCount,
      applyFilters: ({ distributorId }: { distributorId?: number }) => {
        setDealerDocumentFilters((old) => ({ ...old, distributorId, offset: 0 }));
      },
      resetFilters: () => {
        setDealerDocumentFilters({ limit: defaultLimit, offset: defaultOffset, distributorId: undefined });
      },
    },
    views: {
      [ProductDealerDocumentsAction.DELETE]: currentView === ProductDealerDocumentsAction.DELETE,
      [ProductDealerDocumentsAction.ADD]: currentView === ProductDealerDocumentsAction.ADD,
    },
  };
}

export function useProductDealerDocumentProvider() {
  return useDataProviderPartition<ReturnType<typeof useProductDealerDocument>>('productDealerDocumentHook');
}
